import React, { useState, useEffect, useMemo } from 'react';
import { handleEnterNavigation } from '../../utils/fieldNav';
import { parseUnitAware, isPlainNumber } from '../../utils/unitParse';
import { useRecentValues } from '../../hooks/useRecentValues';
import './InputField.css';

interface ValidationResult {
  type: 'error' | 'warning' | null;
  message: string | null;
}

interface InputFieldProps {
  id: string;
  label: string;
  type?: 'text' | 'number';
  value: string | number;
  onChange: (value: string) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  placeholder?: string;
  error?: string;
  warning?: string;
  disabled?: boolean;
  className?: string;
  validate?: (value: string | number) => ValidationResult | null;
  helpText?: string;
  /**
   * Number fields: remember the last few distinct entries under this key
   * (shared across calculators — "length-ft", "motor-hp") and offer them as
   * one-tap chips under the input.
   */
  recentKey?: string;
  /**
   * Number fields where blank means "none" (an optional rating, a limit
   * that is not set): emptying the field reports '' so the editor can clear
   * the value. Off by default — a required number keeps its last value.
   */
  allowEmpty?: boolean;
  /** Text fields: values offered as you type (a datalist), e.g. the locations already used. */
  suggestions?: string[];
}

/**
 * Reusable input field with label and validation feedback
 * For number inputs, maintains internal string state to allow smooth editing
 */
export function InputField({
  id,
  label,
  type = 'text',
  value,
  onChange,
  min,
  max,
  step,
  unit,
  placeholder,
  error,
  warning,
  disabled = false,
  className = '',
  validate,
  helpText,
  recentKey,
  allowEmpty = false,
  suggestions,
}: InputFieldProps) {
  // For number inputs, maintain internal string state to allow free typing
  const [internalValue, setInternalValue] = useState(String(value));
  const [isFocused, setIsFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const [recent, rememberRecent] = useRecentValues(type === 'number' ? recentKey : undefined);
  const currentNumber = type === 'number' ? Number(value) : Number.NaN;
  const recentChips = recent.filter((v) => v !== currentNumber);

  // Live validation
  const validation = useMemo(() => {
    if (!validate || !touched) return null;
    return validate(value);
  }, [validate, value, touched]);

  // Combine explicit error/warning props with validation results
  const displayError = error || (validation?.type === 'error' ? validation.message : null);
  const displayWarning = warning || (validation?.type === 'warning' ? validation.message : null);
  const hasError = !!displayError;
  const hasWarning = !!displayWarning && !hasError;

  // Sync internal value with external value when not focused
  useEffect(() => {
    if (!isFocused) {
      // Legacy focus/editing semantics: syncing the draft text from props here is intentional;
      // deriving it during render would clobber in-progress edits.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInternalValue(String(value));
    }
  }, [value, isFocused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (type === 'number') {
      // Always update internal state for smooth typing
      setInternalValue(newValue);

      // Only call parent onChange if value is a valid number — or, where
      // blank means none, when the field is emptied.
      if (newValue === '' && allowEmpty) {
        onChange('');
      } else if (newValue !== '' && newValue !== '-' && newValue !== '.') {
        const parsed = parseFloat(newValue);
        if (!isNaN(parsed)) {
          onChange(newValue);
        }
      }
    } else {
      // Keep the typed text while focused: a parent that trims or dedupes
      // the value (renameLoad) must not eat a space typed between words
      // ("FAN NO. 4" + " X" read "FAN NO. 4X"; 103-0140).
      setInternalValue(newValue);
      onChange(newValue);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    setTouched(true);
    // On blur, show the parent's value again (what was committed).
    setInternalValue(String(value));
    if (type === 'number' && Number.isFinite(currentNumber)) rememberRecent(currentNumber);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const applyNumber = (n: number) => {
    setInternalValue(String(n));
    onChange(String(n));
  };

  // Pasted "112.5 kVA" / "480Y/277" / "1,200 ft" would be rejected outright
  // by a number input; parse it into the field's unit instead.
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (type !== 'number') return;
    const text = e.clipboardData.getData('text');
    if (!text || isPlainNumber(text)) return;
    const parsed = parseUnitAware(text, unit);
    if (parsed === null) return;
    e.preventDefault();
    applyNumber(parsed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    handleEnterNavigation(e);
  };

  const chipLabel = (v: number) => `${v.toLocaleString('en-US')}${unit ? ` ${unit}` : ''}`;

  // The draft while focused, otherwise the prop value
  const displayValue = isFocused ? internalValue : value;

  return (
    <div className={`input-field ${className} ${hasError ? 'has-error' : ''} ${hasWarning ? 'has-warning' : ''}`}>
      <label htmlFor={id} className="input-label">
        {label}
      </label>
      <div className="input-wrapper">
        <input
          id={id}
          type={type}
          // Phones: the decimal keypad instead of the full keyboard for
          // every numeric entry.
          inputMode={type === 'number' ? 'decimal' : undefined}
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          disabled={disabled}
          className={`input-element ${unit ? 'has-unit' : ''}`}
          list={suggestions && suggestions.length > 0 ? `${id}-suggestions` : undefined}
        />
        {suggestions && suggestions.length > 0 && (
          <datalist id={`${id}-suggestions`}>
            {suggestions.map((v) => <option key={v} value={v} />)}
          </datalist>
        )}
        {unit && <span className="input-unit">{unit}</span>}
      </div>
      {recentChips.length > 0 && !disabled && (
        <div className="input-recent" role="group" aria-label={`Recent ${label} values`}>
          {recentChips.map((v) => (
            <button
              key={v}
              type="button"
              className="input-recent-chip"
              onClick={() => applyNumber(v)}
              title={`Use ${chipLabel(v)}`}
            >
              {chipLabel(v)}
            </button>
          ))}
        </div>
      )}
      {hasError && <span className="input-error">{displayError}</span>}
      {hasWarning && <span className="input-warning">{displayWarning}</span>}
      {helpText && !hasError && !hasWarning && (
        <span className="input-help">{helpText}</span>
      )}
    </div>
  );
}

export default InputField;
